import { FC } from 'react';

import { FlowRadioGroup, getRadioGroupNameProps } from '@eneco/flows/components';
import { requiredRadioChoice } from '@eneco/flows/helpers';
import { RadioTile } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { UsageKnownStepRendering } from '@sitecore/types/UsageKnownStep';
import * as yup from 'yup';

import { StepShell } from '../../components/StepShell/StepShell';
import { useFlowStep } from '../../hooks';

interface FormFields {
  isUsageKnown: string;
}

const toUsageKnownValue = (isUsageKnown?: boolean) => {
  if (isUsageKnown === undefined) return undefined;

  return isUsageKnown ? 'exactUsage' : 'estimateUsage';
};

export const UsageKnownStep: FC = () => {
  const { UsageKnownStep: { fields: textualData } = {} } = usePlaceholderContent<{
    UsageKnownStep: UsageKnownStepRendering;
  }>();

  const {
    form: {
      handleSubmit,
      control,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    buildSchema: () =>
      yup.object({
        isUsageKnown: requiredRadioChoice(textualData?.choice?.usageKnownRadio?.value?.requiredMessage),
      }),
    defaultValues: context => ({
      isUsageKnown: toUsageKnownValue(context.isUsageKnown),
    }),
    onSubmitSuccess: formData => ({
      isUsageKnown: formData.isUsageKnown === 'exactUsage',
    }),
  });

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <FlowRadioGroup
        control={control}
        name="isUsageKnown"
        {...getRadioGroupNameProps(textualData?.choice?.usageKnownRadio?.value?.label)}
        hint={textualData?.choice?.usageKnownRadio?.value?.hint || undefined}
        direction="column"
        error={errors['isUsageKnown']?.message}>
        {textualData?.choice?.usageKnownRadio?.value?.options?.map(option => (
          <RadioTile key={option.name} value={option.name} subtitle={option.description || undefined}>
            {option.label}
          </RadioTile>
        ))}
      </FlowRadioGroup>
    </StepShell>
  );
};
